import * as moment from 'moment';
import { IPaystub, IWatrRequest } from '@nkr/models/index';
import { FileDocumentType } from '@nkr/enums/document-type';
import { IWatrViewerDocument } from '../../../utils/watr-viewer-document';

export interface IPaystubOverview {
    earliestDate: string;
    latestDate: string;
    coverage: string;
    totalGross: number;
    stubCount: number;
    flaggedCount: number;
    missingDates: boolean;
    missingGross: boolean;
}

export const sortPaystubs = (paystubs: IPaystub[]): IPaystub[] => {
    return [...paystubs].sort((a, b) => {
        const seq = (a.paystubSequence ?? 0) - (b.paystubSequence ?? 0);

        if (seq !== 0) {
            return seq;
        }

        const start = moment(a.startDate).valueOf() - moment(b.startDate).valueOf();

        if (start !== 0) {
            return start;
        }

        return (a.id || 0) - (b.id || 0);
    });
};

export const formatCoverageRange = (startText: string, endText: string): string => {
    if (!startText || startText === '-' || !endText || endText === '-') {
        return 'Dates not available';
    }

    const start = moment(startText, 'MM/DD/YYYY');
    const end = moment(endText, 'MM/DD/YYYY');

    if (!start.isValid() || !end.isValid()) {
        return `${startText} – ${endText}`;
    }

    if (start.year() === end.year()) {
        return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }

    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

export const calculatePaystubOverview = (paystubs: IPaystub[], data: IWatrRequest): IPaystubOverview => {
    const flaggedCount = paystubs.filter(paystub => paystub.reviewRequired === true).length;

    if (paystubs.length === 0) {
        const earliestDate = data.incomePayStubStartDate ? moment(data.incomePayStubStartDate).format('MM/DD/YYYY') : '-';
        const latestDate = data.incomePayStubEndDate ? moment(data.incomePayStubEndDate).format('MM/DD/YYYY') : '-';
        const totalGross = data.incomePayStubGross || 0;

        return {
            earliestDate,
            latestDate,
            coverage: formatCoverageRange(earliestDate, latestDate),
            totalGross,
            stubCount: 0,
            flaggedCount: 0,
            missingDates: earliestDate === '-' || latestDate === '-',
            missingGross: !(totalGross > 0)
        };
    }

    const earliestDate = paystubs.reduce((earliest, paystub) => {
        const paystubDate = moment(paystub.startDate);
        return !earliest || paystubDate.isBefore(earliest) ? paystubDate : earliest;
    }, null as moment.Moment | null);

    const latestDate = paystubs.reduce((latest, paystub) => {
        const paystubDate = moment(paystub.endDate);
        return !latest || paystubDate.isAfter(latest) ? paystubDate : latest;
    }, null as moment.Moment | null);

    const totalGross = paystubs.reduce((sum, paystub) => sum + paystub.grossAmount, 0);
    const startText = earliestDate ? earliestDate.format('MM/DD/YYYY') : '-';
    const endText = latestDate ? latestDate.format('MM/DD/YYYY') : '-';

    return {
        earliestDate: startText,
        latestDate: endText,
        coverage: formatCoverageRange(startText, endText),
        totalGross,
        stubCount: paystubs.length,
        flaggedCount,
        missingDates: startText === '-' || endText === '-',
        missingGross: !(totalGross > 0)
    };
};

export const getPaystubVerifyDates = (paystubs: IPaystub[], data: IWatrRequest) => {
    if (paystubs.length > 0) {
        const earliest = paystubs.reduce(
            (earliestDate, paystub) => moment(paystub.startDate).isBefore(moment(earliestDate)) ? paystub.startDate : earliestDate,
            paystubs[0].startDate
        );
        const latest = paystubs.reduce(
            (latestDate, paystub) => moment(paystub.endDate).isAfter(moment(latestDate)) ? paystub.endDate : latestDate,
            paystubs[0].endDate
        );

        return {
            earliestDate: moment(earliest).format('YYYY-MM-DD'),
            latestDate: moment(latest).format('YYYY-MM-DD')
        };
    }

    const startM = moment(data.incomePayStubStartDate);
    const endM = moment(data.incomePayStubEndDate);

    return {
        earliestDate: startM.isValid() ? startM.format('YYYY-MM-DD') : '',
        latestDate: endM.isValid() ? endM.format('YYYY-MM-DD') : ''
    };
};

export const getSelfEmployedVerifyPayload = (taxYear: number, taxGross: number) => {
    const year = Number(taxYear) || 0;
    const gross = Number(taxGross) || 0;

    return {
        earliestDate: year > 0 ? `${year}-01-01` : '',
        latestDate: year > 0 ? `${year}-12-31` : '',
        totalGross: gross,
        incomeIrsYear: year,
        incomeIrsYearGross: gross
    };
};

export const buildSelfEmployedViewerDocs = (data: IWatrRequest): IWatrViewerDocument[] => {
    const docs: IWatrViewerDocument[] = [];
    const requestId = data.id;

    if (data.taxDocumentFile) {
        docs.push({
            docType: FileDocumentType.Tax,
            lookupId: requestId,
            label: 'Tax Document'
        });
    }

    if (data.selfEmployedAffirmationFile) {
        docs.push({
            docType: FileDocumentType.DsSelfEmployedAffidavit,
            lookupId: requestId,
            label: 'Self Employed Affidavit'
        });
    }

    if (docs.length === 0) {
        docs.push({
            docType: FileDocumentType.Tax,
            lookupId: requestId,
            label: 'Tax Document'
        });
    }

    return docs;
};

export const buildPaystubViewerDocs = (sortedPaystubs: IPaystub[], requestId: number): IWatrViewerDocument[] => {
    if (sortedPaystubs.length === 0) {
        return [{
            docType: FileDocumentType.PayStub,
            lookupId: requestId,
            label: 'Pay Stub'
        }];
    }

    return sortedPaystubs.map((paystub, index) => ({
        docType: FileDocumentType.PayStub,
        lookupId: paystub.paystubFileId || requestId,
        label: `Pay Stub ${paystub.paystubSequence || index + 1}`
    }));
};
